import { createAction, props } from '@ngrx/store';
import {
  TTSFloatingTextRequest,
  WidgetData,
  WidgetInstance,
} from '@mhe/reader/models';

// actions are per-widget in this phase of integration
// individual WidgetManager objects will trigger actions
export const widgetDataSearch = createAction(
  '[Widget Data] Search for widget data',
  props<{ widgetUuid: string }>(),
);

export const widgetDataSave = createAction(
  '[Widget Data] Save widget data',
  props<{ widgetUuid: string, dataStringified: string }>(),
);

export const saveWidgetDataSuccess = createAction(
  '[Widget Data] Save widget data - success',
);
export const saveWidgetDataError = createAction(
  '[Widget Data] Save widget data - error',
);

export const resetActivitiesConfirm = createAction(
  '[Widget Data] Ask to confirm reset activities',
  props<{ scope: 'all' | 'exhibit' }>(),
);

export const resetActivities = createAction(
  '[Widget Data] Reset activities',
  props<{ scope: 'all' | 'exhibit' }>(),
);

export const resetIframes = createAction(
  '[Widget Instance] Reset/reload the active widget iframes',
);

export const widgetInstanceResetIframe = createAction(
  '[Widget Instance] Reset/reload a widget iframe',
  props<{ widgetUuid: string, widgetIframeReset: null | 'pending' | 'reset' }>(),
);

export const widgetInstanceAdd = createAction(
  '[Widget Instance] Add Instance',
  props<{ widgetInstance: WidgetInstance }>(),
);

export const widgetInstanceRemove = createAction(
  '[Widget Instance] Remove Instance',
  props<{ widgetUuid: string }>(),
);

export const widgetInstanceUpdateWidgetData = createAction(
  '[Widget Instance] Update widget data on widget intance',
  props<{ widgetUuid: string, widgetData: WidgetData }>(),
);

export const widgetInstanceSelectByWidgetUuid = createAction(
  '[Widget Instance] Get instance by wigetUuid',
  props<{ widgetUuid: string }>(),
);

export const widgetInstanceSetWidgetSearch = createAction(
  '[Widget Instance] Set widgetSearchRequested state',
  props<{ widgetUuid: string, isRequested: boolean }>(),
);

export const widgetInstanceSetPreRender = createAction(
  '[Widget Instance] Set preRendered state',
  props<{ widgetUuid: string, isPreRendered: boolean }>(),
);

export const widgetInstanceSetPostRender = createAction(
  '[Widget Instance] Set postRendered state',
  props<{ widgetUuid: string, isPostRendered: boolean }>(),
);

export const widgetInstanceIframeDomLoaded = createAction(
  '[Widget Instance] Set iframeDomLoaded state',
  props<{ widgetUuid: string, isLoaded: boolean }>(),
);

export const widgetInstanceSetDataRestored = createAction(
  '[Widget Instance] Set dataRestoredOnLoad state',
  props<{ widgetUuid: string, isRestored: boolean }>(),
);

export const widgetInstanceIsSaving = createAction(
  '[Widget Instance] Set widgetDataSaving state',
  props<{
    widgetUuid: string
    isSaving: null | 'saving' | 'saved' | 'not-saved'
  }>(),
);

export const widgetTextToSpeech = createAction(
  '[Widget] Call Readspeaker service for text-to-speech',
  props<{
    ePubIframe: HTMLIFrameElement
    textToSpeechRequest: TTSFloatingTextRequest
  }>(),
);

/**
 * Widget Loading Process Actions
 */
export const widgetSetAcmeSharedReady = createAction(
  '[Widget Instance] Set Widget Acme Shared state',
  props<{ widgetUuid: string, isReady: boolean }>(),
);

export const widgetSetLoadingComplete = createAction(
  '[Widget Instance] Set Widget Loading Complete state',
  props<{ widgetUuid: string, isComplete: boolean }>(),
);

/**
 * Read Aloud + Widget Iframe Feature Integration Actions
 */

// starts the process to get text from all widgets
export const widgetReadAloudGatherTexts = createAction(
  '[Widget Instance] Gather read aloud texts from all widgets',
);

// all widgets have responded or timed out
export const widgetReadAloudTextsReady = createAction(
  '[Widget Instance] Read aloud texts ready from all widgets',
  props<{ hasWidgets: boolean }>(),
);

// an individual widget has requested read aloud text
export const widgetReadAloudTextRequested = createAction(
  '[Widget Instance] Set readAloudTextRequested state',
  props<{ widgetUuid: string, readAloudTextRequested: boolean }>(),
);

// the text received for an individual widget
export const widgetReadAloudTextReceived = createAction(
  '[Widget Instance] Set readAloudTextReceived state',
  props<{ widgetUuid: string, readAloudTextReceived: null | string }>(),
);

export type WidgetDataActions = ReturnType<
| typeof widgetDataSearch
| typeof widgetDataSave
| typeof saveWidgetDataSuccess
| typeof saveWidgetDataError
| typeof widgetInstanceAdd
| typeof widgetInstanceRemove
| typeof widgetInstanceSelectByWidgetUuid
| typeof widgetInstanceSetPreRender
| typeof widgetInstanceSetPostRender
| typeof widgetInstanceIsSaving
| typeof widgetTextToSpeech
| typeof widgetSetAcmeSharedReady
| typeof widgetSetLoadingComplete
| typeof widgetReadAloudGatherTexts
| typeof widgetReadAloudTextsReady
| typeof widgetReadAloudTextRequested
| typeof widgetReadAloudTextReceived
>;
